"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.BlockManager = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _util = require("@blockwell/util");
var _remeda = require("remeda");
var _BlockState = require("./block/BlockState");
var _BlockStyleManager = require("./block/BlockStyleManager");
var _BlockInteractionManager = require("./block/BlockInteractionManager");
var _BlockVariableManager = require("./block/BlockVariableManager");
var _variables = require("@blockwell/variables");
var _variables2 = require("@blockwell/dumbapp/lib/variables");
/**
 * Manages a block's logic that's not specific to its type.
 *
 * BlockManager itself handles hierarchy and context, other features are
 * composed (such as BlockVariableManager).
 */
class BlockManager {
  get context() {
    return this.state.ctx;
  }
  set context(ctx) {
    this.state.ctx = ctx;
  }
  get spec() {
    return this.state.spec;
  }
  get events() {
    return this.state.events;
  }
  get type() {
    return this.state.type;
  }
  get registry() {
    return this.state.registry;
  }
  constructor(spec, _type, registry) {
    (0, _defineProperty2.default)(this, "key", void 0);
    (0, _defineProperty2.default)(this, "state", void 0);
    (0, _defineProperty2.default)(this, "repeated", 0);
    (0, _defineProperty2.default)(this, "parent", null);
    (0, _defineProperty2.default)(this, "children", []);
    (0, _defineProperty2.default)(this, "layout", []);
    (0, _defineProperty2.default)(this, "element", void 0);
    (0, _defineProperty2.default)(this, "style", void 0);
    (0, _defineProperty2.default)(this, "interaction", void 0);
    (0, _defineProperty2.default)(this, "variable", void 0);
    (0, _defineProperty2.default)(this, "emitLayout", (0, _util.debounce)(() => {
      // Debounced to collect multiple changes into a single event
      let layout = [...this.layout];
      this.events.emit("layout", layout);
    }, 0));
    (0, _defineProperty2.default)(this, "variableAdded", variable => {
      if (variable.ref instanceof _variables.VariableReference) {
        let type = variable.ref.type;
        if (type instanceof _variables2.DumbappResultVariable) {
          this.registry.tracker.addVariable(type, this.key);
        }
      }
    });
    (0, _defineProperty2.default)(this, "variableRemoved", variable => {
      if (variable.ref instanceof _variables.VariableReference) {
        let type = variable.ref.type;
        if (type instanceof _variables2.DumbappResultVariable) {
          this.registry.tracker.removeVariable(type);
        }
      }
    });
    (0, _defineProperty2.default)(this, "specUpdated", () => {
      if (this.repeated) {
        this.updateRepeatSpec();
      }
    });
    this.key = registry.blocks.register(this);
    this.state = new _BlockState.BlockState(this.key, spec, _type, registry, this);
    this.style = new _BlockStyleManager.BlockStyleManager(this.state);
    this.interaction = new _BlockInteractionManager.BlockInteractionManager(this.state);
    this.variable = new _BlockVariableManager.BlockVariableManager(this.state);
  }
  getParentBlock() {
    let parent = this.parent;

    // Skip meta-blocks
    while (parent && parent.type.metablock) {
      parent = parent.parent;
    }
    return parent;
  }
  isDescendantOf(block) {
    let parent = this.parent;
    while (parent) {
      if (parent.key === block.key) {
        return true;
      }
      parent = parent.parent;
    }
    return false;
  }
  isAncestorOf(block) {
    let layer;
    layer = this.children;
    while (layer.length > 0) {
      let nextLayer = [];
      for (let it of layer) {
        if (it.key === block.key) {
          return true;
        }
        nextLayer = nextLayer.concat(it.children);
      }
      layer = nextLayer;
    }
    return false;
  }
  hasOwnContext() {
    var _this$context;
    return ((_this$context = this.context) === null || _this$context === void 0 ? void 0 : _this$context.ownerKey) === this.key;
  }
  onValue(listener) {
    this.type.events.on("value", listener);
  }
  offValue(listener) {
    this.type.events.off("value", listener);
  }
  onLayout(listener) {
    this.events.on("layout", listener);
  }
  offLayout(listener) {
    this.events.off("layout", listener);
  }
  destroy() {
    this.type.events.clearListeners();
    this.events.clearListeners();
    if (this.context && this.context.ownerKey === this.key) {
      this.context.stop(true);
    }
  }
  destroyTree() {
    let layer = [this];
    while (layer.length > 0) {
      let nextLayer = [];
      for (let it of layer) {
        it.type.events.clearListeners();
        it.events.clearListeners();
        it.registry.blocks.remove(it);
        nextLayer = nextLayer.concat(it.children);
      }
      layer = nextLayer;
    }
  }
  renderTree(skipSelf = false) {
    let layer;
    if (skipSelf) {
      layer = this.children;
    } else {
      layer = [this];
    }
    while (layer.length > 0) {
      let nextLayer = [];
      for (let it of layer) {
        it.type.renderType();
        nextLayer = nextLayer.concat(it.children);
      }
      layer = nextLayer;
    }
  }
  layoutTree() {
    let layer = [this];
    while (layer.length > 0) {
      let nextLayer = [];
      for (let it of layer) {
        it.emitLayout();
        nextLayer = nextLayer.concat(it.children);
      }
      layer = nextLayer;
    }
  }
  canHaveChildren() {
    return !this.type.noChildren;
  }
  addChild(child, position) {
    if (child.parent) {
      throw new Error(`Child block ${child.key} already has a parent ${child.parent.key}`);
    }
    child.parent = this;
    if (this.type.type === "repeat") {
      child.repeated = this.key;
    } else {
      child.repeated = this.repeated;
    }
    if (position !== undefined && position <= this.children.length) {
      this.children.splice(position, 0, child);
      this.layout.splice(position, 0, Object.freeze({
        block: child
      }));
    } else {
      this.children.push(child);
      this.layout.push(Object.freeze({
        block: child
      }));
    }
    this.emitLayout();
    child.emitLayout();
  }
  emitChange(op) {
    this.registry.change({
      type: "block",
      op: "add",
      key: this.key
    });
  }
  removeChild(child) {
    let index = this.children.indexOf(child);
    this.children.splice(index, 1);
    this.layout.splice(index, 1);
    child.parent = null;
    this.emitLayout();
  }
  remove() {
    this.removeContext();
    this.removeBlock();
  }
  removeBlock() {
    if (this.parent) {
      this.parent.removeChild(this);
    }
  }
  removeContext() {
    if (!this.context) {
      return;
    }
    let layer = [this];
    let parent = this.context;
    let parentKey = this.context.ownerKey;
    if (this.context.ownerKey === this.key) {
      // If this Block has a context, the actual parent we're looking for is the parent.
      // Otherwise, this Block is already pointing to the desired parent Context.
      parent = this.context.parent;
      parentKey = parent.ownerKey;
    }
    while (layer.length > 0) {
      let nextLayer = [];
      for (let block of layer) {
        // First, remove any variables from the parent context registered by this block
        parent.removeVariables(block.key);
        if (block.context.ownerKey === block.key) {
          // If the block owns its context, stop that context
          block.context.stop();
        }
        if (block.context.ownerKey === block.key && block.context.parent.ownerKey === parentKey) {
          // If this block's context is a child of the parent we're removing from,
          // remove it
          parent.removeChild(block.context);
        }
        block.context = undefined;
        block.variable.dynamicVariables = [];
        for (let it of block.type.variables()) {
          block.variableRemoved(it);
        }
        nextLayer = nextLayer.concat(block.children);
      }
      layer = nextLayer;
    }
  }
  static migrateContext(block, from, to) {
    let layer = [block];
    while (layer.length > 0) {
      let nextLayer = [];
      for (let block of layer) {
        if (block.context === from) {
          block.context = to;
        } else if (block.context.parent === from) {
          to.addChild(block.context);
        }
        to.active = false;
        let vars = [...block.type.variables().map(it => it.ref), ...block.variable.dynamicVariables];
        for (let it of vars) {
          if (it instanceof _variables.VariableReference) {
            if (it.resolver === from.resolver) {
              let info = from.removeVariable(it, false);
              to.addVariable(block.key, info.name, it, info.source);
            }
          }
        }
        to.active = true;
        for (let it of vars) {
          if (it instanceof _variables.VariableReference) {
            it.trigger();
          }
        }
        nextLayer = nextLayer.concat(block.children);
      }
      layer = nextLayer;
    }
  }

  /**
   * Add context to an existing hierarchy of blocks. This is called after moving or duplicating
   * a block hierarchy.
   */
  addContext() {
    let layer = [this];
    while (layer.length > 0) {
      let newLayer = [];
      for (let block of layer) {
        var _spec$vars, _block$spec$vars;
        let spec = block.spec;
        let withContext = spec.createCtx || ((_spec$vars = spec.vars) === null || _spec$vars === void 0 ? void 0 : _spec$vars.length) > 0 || spec.type === "repeat-child" || spec.type === "condition";
        if (withContext) {
          if (typeof withContext === "string") {
            block.context = new _variables.ContextManager(block.key, withContext);
          } else {
            block.context = new _variables.ContextManager(block.key);
          }
          block.parent.context.addChild(block.context);
        } else {
          block.context = block.parent.context;
        }
        if (((_block$spec$vars = block.spec.vars) === null || _block$spec$vars === void 0 ? void 0 : _block$spec$vars.length) > 0) {
          block.variable.addVariables();
        }
        block.variable.registerVariables();
        if (block.children) {
          newLayer = newLayer.concat(block.children);
        }
      }
      layer = newLayer;
    }
  }
  ensureContext() {
    // This block doesn't have its own context yet, so create it
    if (this.context.ownerKey !== this.key) {
      let context = new _variables.ContextManager(this.key, typeof this.spec.createCtx === "string" ? this.spec.createCtx : null);
      this.context.addChild(context);
      BlockManager.migrateContext(this, this.context, context);
      return true;
    }
    return false;
  }
  updateContextName(name) {
    if (this.spec.createCtx !== name) {
      if (name) {
        this.spec.createCtx = name;
        this.ensureContext();
      } else {
        delete this.spec.createCtx;
        this.removeContext();
        this.addContext();
      }
      this.registry.change({
        type: "block",
        op: "update",
        key: this.key
      });
    }
  }
  updateParameter(key, value) {
    // Assign first to avoid type issues
    let param = this.type.param;
    let oldValue = (0, _remeda.clone)(param[key]);
    param[key] = value;
    this.variable.updateParameter(key, value, oldValue);
    this.registry.change({
      type: "block",
      op: "update",
      key: this.key
    });
  }
  updateRepeatSpec(child) {
    var _this$parent;
    return ((_this$parent = this.parent) === null || _this$parent === void 0 ? void 0 : _this$parent.updateRepeatSpec(this)) || false;
  }
  findNamedContext(name) {
    if (!name) {
      return null;
    }
    let layer = [this];
    while (layer.length > 0) {
      let nextLayer = [];
      for (let it of layer) {
        if (!it.type.conditional) {
          if (it.context.name === name) {
            return it.context;
          }
          nextLayer = nextLayer.concat(it.children);
        }
      }
      layer = nextLayer;
    }
    return null;
  }
  getRootBlock() {
    let current = this;
    while (current.getParentBlock()) {
      current = current.getParentBlock();
    }
    return current;
  }
  toBlockSpec() {
    return this.spec;
  }
  toSpec() {
    let block = this.toBlockSpec();
    block.children = this.children.map(it => it.toSpec());
    let spec = {
      block
    };
    return spec;
  }
  toJSON() {
    var _this$children, _block$act, _block$vars, _block$classes;
    let block = (0, _remeda.clone)(this.spec);
    if (((_this$children = this.children) === null || _this$children === void 0 ? void 0 : _this$children.length) > 0) {
      block.children = this.children.map(it => it.toJSON());
    } else {
      delete block.children;
    }
    if (((_block$act = block.act) === null || _block$act === void 0 ? void 0 : _block$act.length) === 0) {
      delete block.act;
    }
    if (((_block$vars = block.vars) === null || _block$vars === void 0 ? void 0 : _block$vars.length) === 0) {
      delete block.vars;
    }
    if (((_block$classes = block.classes) === null || _block$classes === void 0 ? void 0 : _block$classes.length) === 0) {
      delete block.classes;
    }
    return {
      block
    };
  }
}
exports.BlockManager = BlockManager;
//# sourceMappingURL=BlockManager.js.map